import type { RefObject } from "react";
import { ArrowRight, X } from "lucide-react";
import type { AvatarHandle } from "@/features/companion";
import { MarcusOnboardingIntroAvatar } from "./MarcusOnboardingIntroAvatar";
import { ONBOARDING_COPY } from "./marcusOnboardingIntroCopy";

export interface MarcusOnboardingIntroDialogProps {
  avatarRef: RefObject<AvatarHandle | null>;
  liveAvatarConnected: boolean;
  liveAvatarSpeaking: boolean;
  voiceIntroActive: boolean;
  primaryActionLabel: string;
  onAccept: () => void;
  onDismiss: () => void;
  onAvatarConnected: () => void;
  onAvatarDisconnected: () => void;
  onAvatarStartSpeaking: () => void;
  onAvatarStopSpeaking: () => void;
}

export function MarcusOnboardingIntroDialog({
  avatarRef,
  liveAvatarConnected,
  liveAvatarSpeaking,
  voiceIntroActive,
  primaryActionLabel,
  onAccept,
  onDismiss,
  onAvatarConnected,
  onAvatarDisconnected,
  onAvatarStartSpeaking,
  onAvatarStopSpeaking,
}: MarcusOnboardingIntroDialogProps) {
  return (
    <section
      role="dialog"
      aria-modal="true"
      aria-labelledby="marcus-onboarding-title"
      aria-describedby="marcus-onboarding-description"
      className="relative max-h-[calc(100dvh-2rem)] w-full max-w-[calc(100vw-2rem)] overflow-hidden overflow-y-auto rounded-[28px] border border-outline-variant/60 bg-surface-lowest/95 p-5 text-foreground shadow-[0_24px_80px_rgba(28,27,27,0.16)] backdrop-blur-[24px] transition-all duration-500 sm:max-h-[calc(100dvh-3rem)] sm:max-w-[500px] sm:p-7"
    >
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
        <MarcusOnboardingIntroAvatar
          avatarRef={avatarRef}
          compact={false}
          liveConnected={liveAvatarConnected}
          liveSpeaking={liveAvatarSpeaking}
          voiceIntroActive={voiceIntroActive}
          onConnected={onAvatarConnected}
          onDisconnected={onAvatarDisconnected}
          onStartSpeaking={onAvatarStartSpeaking}
          onStopSpeaking={onAvatarStopSpeaking}
        />
        <div className="min-w-0 pr-8 max-sm:pr-10">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            {ONBOARDING_COPY.eyebrow}
          </p>
          <h2
            id="marcus-onboarding-title"
            className="mt-2 text-[22px] font-semibold leading-[1.18] text-foreground"
          >
            {ONBOARDING_COPY.title}
          </h2>
          <p
            id="marcus-onboarding-description"
            className="mt-3 max-w-[34rem] text-sm leading-6 text-muted-foreground"
          >
            {ONBOARDING_COPY.description}
          </p>
        </div>
      </div>

      <button
        type="button"
        aria-label="Dismiss interactive onboarding"
        onClick={onDismiss}
        className="absolute right-5 top-5 inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-surface-container hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="mt-7 flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
        <button
          type="button"
          onClick={onDismiss}
          className="inline-flex h-10 max-w-full items-center justify-center rounded-full border border-outline-variant/80 bg-surface-low px-5 text-sm font-medium text-foreground transition-colors hover:bg-surface-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {ONBOARDING_COPY.secondaryAction}
        </button>
        <button
          type="button"
          onClick={onAccept}
          className="inline-flex h-10 max-w-full items-center justify-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-container hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {primaryActionLabel}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </section>
  );
}
